"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Modal } from "@/app/components/Modal";
import { Badge } from "@/app/components/ui/Badge";
import { Button } from "@/app/components/ui/Button";
import { Checkbox } from "@/app/components/ui/Checkbox";
import { Input } from "@/app/components/ui/Input";
import { Select } from "@/app/components/ui/Select";
import type { Company } from "@/app/modules/company/api";
import {
  useCompaniesQuery,
  useCompanyActions,
} from "@/app/modules/company/hooks/useCompanyQueries";
import type { CompanyBranch } from "@/app/modules/directory/api";
import {
  useCompanyBranchActions,
  useCompanyBranchesQuery,
} from "@/app/modules/directory/hooks/useCompanyBranchStructure";
import type { WorkspacePresetCatalogItem } from "../api";
import { useApplyWorkspacePresetMutation } from "../hooks/useApplyWorkspacePresetMutation";
import { useWorkspaceApplicationCatalogQuery } from "../hooks/useWorkspaceApplicationCatalogQuery";
import {
  isConfigurablePresetCode,
  presetApplyConfiguration,
} from "../presetConfigurationRegistry";
import {
  initialPresetRecommendedAppCodes,
} from "../restaurantPresetApplySetup";
import { presetApplyErrorMessage } from "../presetApplyFeedback";

type ModalDraft = {
  companyUuid: string;
  newBranchName: string;
  newCompanyName: string;
  selectedRecommendedAppCodes: string[];
  targetUuid: string;
};

type TargetCreationError = "company" | "branch" | null;

function recommendedAppClasses(selected: boolean): string {
  return [
    "group flex min-h-[4.25rem] cursor-pointer items-center gap-3 rounded-xl border px-4 py-3",
    "transition-[border-color,background-color] duration-150 motion-reduce:transition-none",
    selected
      ? "border-primary bg-primary-subtle/70"
      : "border-border bg-bg-elevated hover:border-border-strong hover:bg-surface-hover dark:hover:bg-bg-sunken/50",
  ].join(" ");
}

function loadDraft(key: string): ModalDraft | null {
  if (typeof window === "undefined") return null;

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(key) ?? "null") as unknown;

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

    const value = parsed as Record<string, unknown>;

    if (
      typeof value.companyUuid !== "string"
      || typeof value.targetUuid !== "string"
      || !Array.isArray(value.selectedRecommendedAppCodes)
      || !value.selectedRecommendedAppCodes.every((item) => typeof item === "string")
    ) {
      return null;
    }

    return {
      companyUuid: value.companyUuid,
      newBranchName: typeof value.newBranchName === "string"
        ? value.newBranchName.slice(0, 255)
        : "",
      newCompanyName: typeof value.newCompanyName === "string"
        ? value.newCompanyName.slice(0, 255)
        : "",
      selectedRecommendedAppCodes: value.selectedRecommendedAppCodes,
      targetUuid: value.targetUuid,
    };
  } catch {
    return null;
  }
}

export function PresetApplyModal({
  initialCompanyName,
  marketingIntentWorkspaceName,
  onApplied,
  onClose,
  open,
  preset,
  workspaceUuid,
}: {
  initialCompanyName?: string;
  marketingIntentWorkspaceName?: string;
  onApplied?: () => void;
  onClose: () => void;
  open: boolean;
  preset: WorkspacePresetCatalogItem;
  workspaceUuid: string;
}) {
  const t = useTranslations("apps.workflows.app-lifecycle");
  const messages = useTranslations();
  const draftKey = `workspace-preset-draft:${workspaceUuid}:${preset.code}`;
  const [initialDraft] = useState(() => loadDraft(draftKey));
  const [companyUuid, setCompanyUuid] = useState(initialDraft?.companyUuid ?? "");
  const [targetUuid, setTargetUuid] = useState(initialDraft?.targetUuid ?? "");
  const [newCompanyName, setNewCompanyName] = useState(initialDraft?.newCompanyName ?? "");
  const [newBranchName, setNewBranchName] = useState(initialDraft?.newBranchName ?? "");
  const [isApplying, setIsApplying] = useState(false);
  const applyRunningRef = useRef(false);
  const [selectedRecommendedAppCodes, setSelectedRecommendedAppCodes] = useState<string[]>(
    initialPresetRecommendedAppCodes(
      preset.recommendedAppCodes,
      initialDraft?.selectedRecommendedAppCodes,
    ),
  );
  const [targetCreationError, setTargetCreationError] = useState<TargetCreationError>(null);
  const [createdCompany, setCreatedCompany] = useState<Company | null>(null);
  const [createdBranch, setCreatedBranch] = useState<CompanyBranch | null>(null);
  const companiesQuery = useCompaniesQuery(workspaceUuid);
  const companyActions = useCompanyActions(workspaceUuid);
  const branchesQuery = useCompanyBranchesQuery(workspaceUuid, companyUuid);
  const branchActions = useCompanyBranchActions(workspaceUuid);
  const applicationCatalogQuery = useWorkspaceApplicationCatalogQuery(workspaceUuid);
  const applyMutation = useApplyWorkspacePresetMutation(workspaceUuid);
  const presetTitle = messages(`${preset.translationKey}.title`);
  const companies = companiesQuery.data ?? [];
  const branches = (branchesQuery.data ?? []).filter((branch) => !branch.archivedAt);
  const selectableCompanies = createdCompany
    && !companies.some((company) => company.uuid === createdCompany.uuid)
    ? [...companies, createdCompany]
    : companies;
  const selectableBranches = createdBranch
    && !branches.some((branch) => branch.uuid === createdBranch.uuid)
    ? [...branches, createdBranch]
    : branches;
  const selectedCompany = companies.find((company) => company.uuid === companyUuid)
    ?? (createdCompany?.uuid === companyUuid ? createdCompany : undefined);
  const branchSelected = selectableBranches.some((branch) => branch.uuid === targetUuid);
  const createsCompany = newCompanyName.trim() !== "";
  const createsBranch = newBranchName.trim() !== "";
  const companyReady = createsCompany || selectedCompany !== undefined;
  const targetReady = preset.targetType === "company"
    ? companyReady
    : companyReady && (createsBranch || (!createsCompany && branchSelected));
  const targetCreationPending = companyActions.isPending || branchActions.isPending;
  const busy = isApplying || targetCreationPending || applyMutation.isPending;
  const prefilledCompanyNameRef = useRef(false);

  useEffect(() => {
    if (
      initialDraft !== null
      || initialCompanyName === undefined
      || prefilledCompanyNameRef.current
      || companiesQuery.isLoading
      || companiesQuery.isError
      || companies.length > 0
      || newCompanyName !== ""
    ) {
      return;
    }

    prefilledCompanyNameRef.current = true;
    setNewCompanyName(initialCompanyName);
  }, [companies.length, companiesQuery.isError, companiesQuery.isLoading, initialCompanyName, initialDraft, newCompanyName]);

  useEffect(() => {
    if (!open) return;

    const draft: ModalDraft = {
      companyUuid,
      newBranchName,
      newCompanyName,
      selectedRecommendedAppCodes,
      targetUuid,
    };
    window.sessionStorage.setItem(draftKey, JSON.stringify(draft));
  }, [
    companyUuid,
    draftKey,
    newBranchName,
    newCompanyName,
    open,
    selectedRecommendedAppCodes,
    targetUuid,
  ]);

  const appTitles = useMemo(
    () => new Map(
      (applicationCatalogQuery.data?.applications ?? []).map((application) => [
        application.appCode,
        messages(`${application.translationKey}.title`),
      ]),
    ),
    [applicationCatalogQuery.data?.applications, messages],
  );

  function appTitle(appCode: string): string {
    return appTitles.get(appCode) ?? appCode;
  }

  function closeModal() {
    if (!busy) onClose();
  }

  function toggleRecommended(appCode: string) {
    setSelectedRecommendedAppCodes((current) => (
      current.includes(appCode)
        ? current.filter((code) => code !== appCode)
        : [...current, appCode]
    ));
  }

  async function applyPreset() {
    if (!targetReady || busy || applyRunningRef.current) return;

    let operation: TargetCreationError = null;

    applyRunningRef.current = true;
    setTargetCreationError(null);
    setIsApplying(true);

    try {
      let resolvedCompanyUuid = companyUuid;
      let resolvedTargetUuid = preset.targetType === "company" ? companyUuid : targetUuid;

      if (createsCompany) {
        operation = "company";
        const company = await companyActions.createCompany({
          name: newCompanyName.trim(),
        });
        resolvedCompanyUuid = company.uuid;
        resolvedTargetUuid = company.uuid;
        setCreatedCompany(company);
        setCreatedBranch(null);
        setCompanyUuid(company.uuid);
        setTargetUuid("");
        setNewCompanyName("");
      }

      if (preset.targetType === "company_branch" && createsBranch) {
        operation = "branch";
        const branch = await branchActions.create(resolvedCompanyUuid, {
          name: newBranchName.trim(),
        });
        resolvedTargetUuid = branch.uuid;
        setCreatedBranch(branch);
        setCompanyUuid(resolvedCompanyUuid);
        setTargetUuid(branch.uuid);
        setNewBranchName("");
      }

      operation = null;
      await applyMutation.mutateAsync({
        presetCode: preset.code,
        data: {
          targetUuid: resolvedTargetUuid,
          selectedRecommendedAppCodes,
          configuration: presetApplyConfiguration(preset.code),
        },
      });
      window.sessionStorage.removeItem(draftKey);
      onApplied?.();

      onClose();
    } catch {
      setIsApplying(false);

      if (operation) {
        setTargetCreationError(operation);
      }
    } finally {
      applyRunningRef.current = false;
    }
  }

  if (!isConfigurablePresetCode(preset.code)) return null;

  const feedback = targetCreationError || applyMutation.isError ? (
    <div className="space-y-2" role="alert">
      {targetCreationError ? (
        <div className="flash-error">
          {t(`wizard.${targetCreationError}CreateError`)}
        </div>
      ) : null}
      {applyMutation.isError ? (
        <div className="flash-error">
          {presetApplyErrorMessage(applyMutation.error, t("wizard.applyError"))}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <Modal
      contentClassName="max-h-[calc(100dvh-8rem)] overflow-y-auto px-5 py-5 sm:px-6 sm:py-6"
      open={open}
      size="lg"
      title={t("wizard.title", { preset: presetTitle })}
      onClose={closeModal}
    >
      <div aria-busy={busy ? "true" : undefined} className="space-y-6">
          {marketingIntentWorkspaceName ? (
            <p className="rounded-xl border border-primary/20 bg-primary-subtle/50 px-4 py-3 text-sm leading-6 text-text-secondary">
              {t("wizard.marketingIntentDescription", {
                preset: presetTitle,
                workspace: marketingIntentWorkspaceName,
              })}
            </p>
          ) : null}
          <section>
            <h3 className="text-base font-semibold text-text">
              {t("wizard.targetQuestion")}
            </h3>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {companiesQuery.isLoading ? (
                <p className="rounded-xl border border-border bg-bg-elevated px-4 py-5 text-sm text-text-secondary sm:col-span-2" role="status">
                  {t("wizard.targetLoading")}
                </p>
              ) : companiesQuery.isError ? (
                <div className="rounded-xl border border-danger/25 bg-danger-subtle px-4 py-4 text-sm text-danger sm:col-span-2" role="alert">
                  <p>{t("wizard.targetLoadError")}</p>
                  <Button
                    className="mt-3"
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => void companiesQuery.refetch()}
                  >
                    {t("wizard.retryTarget")}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {selectableCompanies.length > 0 ? (
                      <Select
                        className="space-y-2"
                        label={t("wizard.selectCompany")}
                        value={companyUuid}
                        onChange={(event) => {
                          const nextCompanyUuid = event.target.value;

                          if (createdCompany?.uuid !== nextCompanyUuid) {
                            setCreatedCompany(null);
                          }
                          setCreatedBranch(null);
                          setCompanyUuid(nextCompanyUuid);
                          setTargetUuid("");
                          setNewCompanyName("");
                          setNewBranchName("");
                        }}
                      >
                        <option value="">{t("wizard.selectCompanyPlaceholder")}</option>
                        {selectableCompanies.map((company) => (
                          <option key={company.uuid} value={company.uuid}>{company.name}</option>
                        ))}
                      </Select>
                    ) : null}

                    <div className={selectableCompanies.length > 0 ? "border-t border-border pt-4" : ""}>
                      <h4 className="mb-3 text-sm font-semibold text-text">
                        {t("wizard.companyCreateQuestion")}
                      </h4>
                      <Input
                        disabled={busy}
                        label={t("wizard.newCompanyName")}
                        maxLength={255}
                        required={selectableCompanies.length === 0}
                        value={newCompanyName}
                        onChange={(event) => {
                          const name = event.target.value;

                          setNewCompanyName(name);
                          setTargetCreationError(null);

                          if (name.trim() !== "") {
                            setCreatedCompany(null);
                            setCreatedBranch(null);
                            setCompanyUuid("");
                            setTargetUuid("");
                          }
                        }}
                      />
                    </div>
                  </div>

                  {preset.targetType === "company_branch" && companyReady ? (
                    <div className="space-y-4">
                      {selectableBranches.length > 0 || branchesQuery.isLoading ? (
                        <Select
                          className="space-y-2"
                          disabled={branchesQuery.isLoading}
                          label={t("wizard.selectBranch")}
                          value={targetUuid}
                          onChange={(event) => {
                            const nextTargetUuid = event.target.value;

                            if (createdBranch?.uuid !== nextTargetUuid) {
                              setCreatedBranch(null);
                            }
                            setTargetUuid(nextTargetUuid);
                            setNewBranchName("");
                          }}
                        >
                          <option value="">{t("wizard.selectBranchPlaceholder")}</option>
                          {selectableBranches.map((branch) => (
                            <option key={branch.uuid} value={branch.uuid}>{branch.name}</option>
                          ))}
                        </Select>
                      ) : null}
                      {branchesQuery.isError ? (
                        <div className="text-sm text-danger" role="alert">
                          <p>{t("wizard.branchesLoadError")}</p>
                          <button
                            className="mt-1 font-semibold underline underline-offset-2"
                            type="button"
                            onClick={() => void branchesQuery.refetch()}
                          >
                            {t("wizard.retryTarget")}
                          </button>
                        </div>
                      ) : null}

                      {!branchesQuery.isLoading && !branchesQuery.isError ? (
                        <div className={selectableBranches.length > 0 ? "border-t border-border pt-4" : ""}>
                          <h4 className="mb-3 text-sm font-semibold text-text">
                            {t("wizard.branchCreateQuestion")}
                          </h4>
                          <Input
                            disabled={busy}
                            label={t("wizard.newBranchName")}
                            maxLength={255}
                            required={selectableBranches.length === 0}
                            value={newBranchName}
                            onChange={(event) => {
                              const name = event.target.value;

                              setNewBranchName(name);
                              setTargetCreationError(null);

                              if (name.trim() !== "") {
                                setCreatedBranch(null);
                                setTargetUuid("");
                              }
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>

          <section className="border-t border-border pt-5">
            <p className="text-sm leading-6 text-text-secondary">
              {t("wizard.defaultApplicationsDescription")}
            </p>

            <details className="mt-3 rounded-xl border border-border bg-bg-sunken/30 px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary">
                {t("wizard.customInstallation")}
              </summary>
              <div className="mt-4 space-y-5">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-text-muted">
                    {t("presets.requiredApps")}
                  </h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {preset.requiredAppCodes.map((appCode) => (
                      <Badge className="!rounded-lg !px-3 !py-1.5" key={appCode}>
                        {appTitle(appCode)}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-text-muted">
                    {t("presets.recommendedApps")}
                  </h4>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {preset.recommendedAppCodes.map((appCode) => {
                      const selected = selectedRecommendedAppCodes.includes(appCode);

                      return (
                        <label className={recommendedAppClasses(selected)} key={appCode}>
                          <Checkbox
                            checked={selected}
                            onChange={() => toggleRecommended(appCode)}
                          />
                          <span className="text-sm font-semibold text-text">
                            {appTitle(appCode)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </details>
          </section>

          {feedback}

          <footer className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
            <Button disabled={busy} type="button" variant="secondary" onClick={closeModal}>
              {t("wizard.cancel")}
            </Button>
            <Button
              disabled={!targetReady}
              isLoading={busy}
              type="button"
              onClick={() => void applyPreset()}
            >
              {t("wizard.apply")}
            </Button>
          </footer>
      </div>
    </Modal>
  );
}
